import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import { Input } from '@chakra-ui/input'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Text as ChakraText, VStack } from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik, Form } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import type { Req } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type { UserSessionResponse } from '../../interfaces'

interface Props {
  isOpen: boolean
  url: string | null
  urlId: string | null
  user: UserSessionResponse | null
  request: Req
  requestMethod: string
  onClose(): void
  refreshPerformanceItems(): void
  label: string
}

interface FormProps {
  url: string
  label: string
}

const TrackUrlModal = ({
  isOpen,
  onClose,
  refreshPerformanceItems,
  url,
  urlId,
  user,
  request,
  requestMethod,
  label
}: Props): React.ReactElement => {
  const toast = useToast()
  const { t } = useTranslation()

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{ url: url ?? '', label: label ?? '' }}
          onSubmit={async (values): Promise<void> => {
            if (requestMethod === 'post' && user?.clientNumber) {
              const response = await request.post(`/api/performance/${user.clientNumber}/url`, {
                url: values.url,
                label: values.label
              })
              if (!request.error) {
                refreshPerformanceItems()
              } else {
                const errorMsg = response?.reason || t(
                  'something_went_wrong',
                  'Something went wrong. Please try again.',
                );
                toast({
                  description: errorMsg,
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              }
              onClose()
            } else if (requestMethod === 'put' && user?.clientNumber) {
              await request.put(
                `/api/performance/${user.clientNumber}/url/${urlId}`,
                {
                  url: values.url,
                  label: values.label
                },
              )
              if (!request.error) {
                refreshPerformanceItems()
              } else {
                toast({
                  description: t(
                    'something_went_wrong',
                    'Something went wrong. Please try again.',
                  ),
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              }
              onClose()
            }
          }}
          validationSchema={Yup.object().shape({
            url: Yup.string()
              .required('Required')
              .matches(
                /^((https?:\/\/)?[\w-]+(\.[\w-]+)+\.?(:\d+)?(\/\S*)?)$/giu,
                'Please enter a valid URL',
              ),
            label: Yup.string().required('Required'),
          })}
        >
          {({ isSubmitting, submitForm, isValid, dirty }) => (
            <>
              <ModalHeader>{t('track_new_url', 'Track new URL')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Form>
                  <VStack spacing={4} alignItems="flex-start">

                    <Field name="label">
                      {({ field, form }: FieldProps<string, FormProps>) => (
                        <FormControl
                          isInvalid={Boolean(
                            form.errors.label && form.touched.label,
                          )}
                        >
                          <FormLabel>
                            <ChakraText
                              fontSize="16px"
                              fontWeight="medium"
                              color={colors.appSlate}
                              lineHeight={1.25}
                              marginBottom="0.5em"
                            >
                              Label
                              <sup>*</sup>
                            </ChakraText>
                          </FormLabel>
                          <Input
                            {...field}
                            placeholder="An identifier for the URL"
                          />
                          <FormErrorMessage>
                            {form.errors.label}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    <Field name="url">
                      {({ field, form }: FieldProps<string, FormProps>) => (
                        <FormControl
                          isInvalid={Boolean(
                            form.errors.url && form.touched.url,
                          )}
                        >
                          <FormLabel>
                            <ChakraText
                              fontSize="16px"
                              fontWeight="medium"
                              color={colors.appSlate}
                              lineHeight={1.25}
                              marginBottom="0.5em"
                            >
                              {t('page_url', 'Page URL')}
                              <sup>*</sup>
                            </ChakraText>
                          </FormLabel>
                          <Input
                            {...field}
                            disabled={requestMethod === 'put'}
                            placeholder="The URL you want us to monitor"
                          />
                          <FormErrorMessage>
                            {form.errors.url}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                  </VStack>
                </Form>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={onClose} variant="outline">
                  Cancel
                </Button>
                <Button
                  isDisabled={!isValid || !dirty}
                  type="submit"
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  Submit
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default TrackUrlModal
